import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { tagChipStyle } from "@/lib/tagColor";
import { formatPhoneFR } from "@/lib/phone";
import { toast } from "sonner";
import { Plus, UserPlus, Phone, Mail, MessageCircle, CalendarPlus, X, ArrowUpRight, ChevronRight, MapPin } from "lucide-react";
import { useConfirm } from "@/context/ConfirmContext";
import { useCreerRdv } from "@/components/CreerRdv";
import NewPatientPanel from "@/components/NewPatientPanel";

export const STATUT = {
  nouveau: { label: "Nouveau", cls: "sx-badge-violet" },
  rdv_avenir: { label: "RDV à venir", cls: "sx-badge-green" },
  a_planifier: { label: "À suivre", cls: "sx-badge-blue" },
  a_relancer: { label: "À relancer", cls: "sx-badge-amber" },
};
const TABS = [
  { key: "tous", label: "Tous", dot: "var(--muted-2)" },
  { key: "a_relancer", label: "À relancer", dot: "oklch(0.72 0.14 65)" },
  { key: "a_planifier", label: "À suivre", dot: "oklch(0.62 0.13 255)" },
  { key: "rdv_avenir", label: "RDV à venir", dot: "oklch(0.62 0.13 155)" },
  { key: "nouveau", label: "Nouveau", dot: "oklch(0.62 0.13 300)" },
];

const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
const digits = (s) => (s || "").replace(/\D/g, "");
export const relDate = (iso) => {
  if (!iso) return null;
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  if (days <= 0) return "Aujourd'hui";
  if (days === 1) return "Hier";
  if (days < 30) return `Il y a ${days} j`;
  const m = Math.floor(days / 30);
  return m < 12 ? `Il y a ${m} mois` : `Il y a ${Math.floor(m / 12)} an(s)`;
};
export const exactDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR") : "");
export const rdvFmt = (iso) => {
  const d = new Date(iso);
  return `${d.toLocaleDateString("fr-FR", { weekday: "short", day: "numeric", month: "short" })} · ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
};

export default function Patients() {
  const nav = useNavigate();
  const confirm = useConfirm();
  const { openCreerRdv } = useCreerRdv();
  const [params, setParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [filtre, setFiltre] = useState(params.get("filtre") || "tous");
  const [secteur, setSecteur] = useState("");
  const [secteurs, setSecteurs] = useState([]);
  const [tag, setTag] = useState("");
  const [tags, setTags] = useState([]);
  const [newOpen, setNewOpen] = useState(false);
  const [newPrefill, setNewPrefill] = useState("");
  const [highlightId, setHighlightId] = useState(null);
  const openNew = (txt) => { setNewPrefill(txt || ""); setApercu(null); setNewOpen(true); };
  const [apercu, setApercu] = useState(params.get("apercu") || null);
  const [sortDir, setSortDir] = useState("desc");
  const [sortKey, setSortKey] = useState("dernier_contact");
  const [dupHidden, setDupHidden] = useState(false);
  const [dupFilter, setDupFilter] = useState(false);
  const [sansEmail, setSansEmail] = useState(params.get("sansEmail") === "1");
  const ctmapRef = useRef({});

  const load = async () => {
    const { data } = await api.get("/patients", { params: { tag, secteur } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tag, secteur]);
  useEffect(() => {
    api.get("/settings/tags").then((r) => setTags(r.data)).catch(() => {});
    api.get("/settings/secteurs").then((r) => setSecteurs(r.data)).catch(() => {});
    api.get("/settings/care-types").then((r) => { ctmapRef.current = Object.fromEntries((r.data || []).map((c) => [c.id, c.nom])); }).catch(() => {});
    /* eslint-disable-next-line */
  }, []);

  // Ouverture du volet « Nouveau patient » depuis l'URL (bouton ⌘K / action Nouveau patient).
  useEffect(() => {
    if (params.get("newpatient")) {
      setNewPrefill(params.get("npq") || "");
      setApercu(null); setNewOpen(true);
      const p = {}; if (filtre !== "tous") p.filtre = filtre;
      setParams(p, { replace: true });
    }
    /* eslint-disable-next-line */
  }, [params]);

  // Touche N (Patientèle, hors champ) : ouvre le volet Nouveau patient (capture → prioritaire sur Créer RDV).
  useEffect(() => {
    const onKey = (e) => {
      if (e.defaultPrevented) return;
      const t = e.target; const tg = (t?.tagName || "").toLowerCase();
      if (tg === "input" || tg === "textarea" || tg === "select" || t?.isContentEditable) return;
      if (document.querySelector("[role='dialog']")) return;
      if ((e.key || "").toLowerCase() === "n" && !e.metaKey && !e.ctrlKey && !e.altKey && !e.shiftKey && !newOpen) {
        e.preventDefault(); e.stopPropagation(); openNew("");
      }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    /* eslint-disable-next-line */
  }, [newOpen]);

  // Surlignage éphémère d'une ligne nouvellement créée (Créer + un autre).
  useEffect(() => {
    if (!highlightId) return;
    const t = setTimeout(() => setHighlightId(null), 2000);
    return () => clearTimeout(t);
  }, [highlightId]);

  // Sync URL
  useEffect(() => {
    const p = {};
    if (filtre !== "tous") p.filtre = filtre;
    if (apercu) p.apercu = apercu;
    setParams(p, { replace: true });
    /* eslint-disable-next-line */
  }, [filtre, apercu]);

  const counts = useMemo(() => {
    const c = { tous: items.length, a_relancer: 0, a_planifier: 0, rdv_avenir: 0, nouveau: 0 };
    items.forEach((p) => { if (c[p.statut] !== undefined) c[p.statut] += 1; });
    return c;
  }, [items]);

  const dup = useMemo(() => {
    const nameMap = {}, phoneMap = {}, ids = new Set();
    items.forEach((p) => {
      const nk = `${norm(p.prenom)}|${norm(p.nom)}`;
      if (nk !== "|") (nameMap[nk] = nameMap[nk] || []).push(p);
      const ph = digits(p.telephone);
      if (ph.length >= 6) (phoneMap[ph] = phoneMap[ph] || []).push(p);
    });
    const examples = [];
    Object.values(nameMap).forEach((g) => { if (g.length > 1) { g.forEach((p) => ids.add(p.id)); if (examples.length < 2) examples.push(`${g[0].prenom} ${g[0].nom} apparaît ${g.length} fois`); } });
    Object.entries(phoneMap).forEach(([ph, g]) => { if (g.length > 1) { g.forEach((p) => ids.add(p.id)); if (examples.length < 2) examples.push(`${g.length} patients partagent le ${p_fmt(ph)}`); } });
    return { ids, examples };
  }, [items]);

  const filtered = useMemo(() => {
    let list = items;
    if (dupFilter) list = list.filter((p) => dup.ids.has(p.id));
    else if (filtre !== "tous") list = list.filter((p) => p.statut === filtre);
    if (sansEmail) list = list.filter((p) => !(p.email || "").trim());
    const dirMul = sortDir === "desc" ? -1 : 1;
    return [...list].sort((a, b) => {
      const av = a[sortKey] || "", bv = b[sortKey] || "";
      if (av === bv) return 0;
      if (!av) return 1; if (!bv) return -1;
      return av < bv ? -dirMul : dirMul;
    });
  }, [items, filtre, dupFilter, dup, sortKey, sortDir, sansEmail]);

  const toggleSort = (k) => { if (sortKey === k) setSortDir((d) => (d === "desc" ? "asc" : "desc")); else { setSortKey(k); setSortDir("desc"); } };

  // Clavier sur volet flottant
  useEffect(() => {
    if (!apercu) return;
    const onKey = (e) => {
      const t = e.target; const tag = (t?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || t?.isContentEditable) return;
      const idx = filtered.findIndex((p) => p.id === apercu);
      if (e.key === "Escape") { setApercu(null); }
      else if (e.key === "ArrowDown") { e.preventDefault(); const n = filtered[Math.min(idx + 1, filtered.length - 1)]; if (n) setApercu(n.id); }
      else if (e.key === "ArrowUp") { e.preventDefault(); const n = filtered[Math.max(idx - 1, 0)]; if (n) setApercu(n.id); }
      else if (e.key === "Enter") { nav(`/patients/${apercu}`); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [apercu, filtered, nav]);

  return (
    <div>
      {/* Bandeau doublons */}
      {!dupHidden && dup.examples.length > 0 && (
        <div className="sx-alert mb-3 flex items-center gap-3" data-testid="dup-banner">
          <span className="flex-1"><span className="font-semibold">{dup.ids.size} doublons possibles</span> · {dup.examples.join(" · ")}</span>
          <button data-testid="dup-verify" onClick={() => { setDupFilter(true); setFiltre("tous"); }} className="sx-link text-[13px]">Vérifier et fusionner</button>
          <button data-testid="dup-close" onClick={() => setDupHidden(true)} className="sx-icon-btn"><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Barre d'outils */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="flex flex-wrap items-center gap-1.5">
          {TABS.map((t) => {
            const active = !dupFilter && filtre === t.key;
            return (
              <button key={t.key} data-testid={`ptab-filter-${t.key}`} onClick={() => { setDupFilter(false); setFiltre(t.key); }}
                className="inline-flex items-center gap-2 px-2.5 py-1.5 rounded-[7px] text-[13px] transition-colors"
                style={{ background: active ? "#fff" : "transparent", border: active ? "1px solid var(--border-input)" : "1px solid transparent", fontWeight: active ? 600 : 500, color: "var(--text-2)" }}>
                <span className="w-[7px] h-[7px] rounded-full" style={{ background: t.dot }} />
                {t.label}<span className="mono text-[11px]" style={{ opacity: 0.6 }}>{counts[t.key] ?? 0}</span>
              </button>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <FilterSelect testid="patient-secteur" value={secteur} onChange={setSecteur} placeholder="Secteur" options={secteurs.map((s) => ({ v: s.id, l: s.nom }))} />
          <FilterSelect testid="patient-tag" value={tag} onChange={setTag} placeholder="Tags" options={tags.map((t) => ({ v: t.id, l: t.titre }))} />
          <button data-testid="new-patient-btn" onClick={() => openNew("")} className="sx-btn-secondary"><UserPlus className="w-[15px] h-[15px]" />Patient</button>
        </div>
      </div>

      <div className="flex gap-4 items-start">
        {/* Tableau */}
        <div className="flex-1 min-w-0 sx-card overflow-hidden">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr style={{ background: "var(--table-head)" }}>
                <th className="text-left px-4 py-2.5 text-[11.5px] font-medium" style={{ color: "var(--muted-2)" }}>Patient</th>
                {!apercu && <th className="text-left px-4 py-2.5 text-[11.5px] font-medium hidden md:table-cell" style={{ color: "var(--muted-2)" }}>Contact</th>}
                <th className="text-left px-4 py-2.5 text-[11.5px] font-medium cursor-pointer select-none hidden sm:table-cell" style={{ color: "var(--muted-2)" }} onClick={() => toggleSort("dernier_contact")}>Dernier contact{sortKey === "dernier_contact" ? (sortDir === "desc" ? " ↓" : " ↑") : ""}</th>
                {!apercu && <th className="text-left px-4 py-2.5 text-[11.5px] font-medium cursor-pointer select-none hidden lg:table-cell" style={{ color: "var(--muted-2)" }} onClick={() => toggleSort("prochain_rdv")}>Prochain RDV{sortKey === "prochain_rdv" ? (sortDir === "desc" ? " ↓" : " ↑") : ""}</th>}
                <th className="text-left px-4 py-2.5 text-[11.5px] font-medium" style={{ color: "var(--muted-2)" }}>Statut · tags</th>
                <th className="px-4 py-2.5" style={{ width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && <tr><td colSpan={6} className="px-4 py-12 text-center" style={{ color: "var(--faint)" }}>Aucun patient. Créez votre premier patient.</td></tr>}
              {filtered.map((p) => {
                const sel = apercu === p.id;
                const st = STATUT[p.statut] || STATUT.nouveau;
                return (
                  <tr key={p.id} data-testid={`patient-row-${p.id}`} className="group cursor-pointer transition-colors align-top"
                    style={{ borderTop: "1px solid var(--divider)", background: p.id === highlightId ? "#fdf8e1" : (sel ? "var(--row-selected)" : ""), boxShadow: sel ? "inset 3px 0 0 var(--accent)" : "" }}
                    onMouseEnter={(e) => { if (!sel && p.id !== highlightId) e.currentTarget.style.background = "var(--row-hover)"; }}
                    onMouseLeave={(e) => { if (!sel && p.id !== highlightId) e.currentTarget.style.background = ""; }}
                    onClick={() => setApercu(p.id)}
                    onDoubleClick={() => nav(`/patients/${p.id}`)}>
                    <td className="px-4 py-2.5">
                      <div className="text-[13.5px] font-semibold whitespace-nowrap" style={{ color: "var(--ink)" }}>{p.prenom} {p.nom}</div>
                      <div className="flex items-center gap-1.5">
                        <span className="mono text-[11px]" style={{ color: "var(--id)" }}>{p.id}</span>
                        {dup.ids.has(p.id) && <span className="sx-badge sx-badge-amber" style={{ padding: "1px 6px", fontSize: "10px" }}>doublon ?</span>}
                      </div>
                    </td>
                    {!apercu && (
                      <td className="px-4 py-2.5 hidden md:table-cell" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1.5 text-[12.5px]" style={{ color: "var(--text-2)" }}>
                          <Phone className="w-3 h-3" style={{ color: "var(--muted-2)" }} />{p.telephone ? formatPhoneFR(p.telephone) : <button onClick={() => nav(`/patients/${p.id}`)} style={{ color: "var(--faint)" }}>+ ajouter</button>}
                        </div>
                        <div className="flex items-center gap-1.5 text-[12.5px] max-w-[220px]" style={{ color: "var(--text-2)" }}>
                          <Mail className="w-3 h-3 shrink-0" style={{ color: "var(--muted-2)" }} /><span className="truncate">{p.email || <button onClick={() => nav(`/patients/${p.id}`)} style={{ color: "var(--faint)" }}>+ ajouter email</button>}</span>
                        </div>
                      </td>
                    )}
                    <td className="px-4 py-2.5 hidden sm:table-cell">
                      {p.dernier_contact ? (
                        <><div className="font-medium" style={{ color: "var(--text-2)" }}>{relDate(p.dernier_contact)}</div><div className="mono text-[11.5px]" style={{ color: "var(--id)" }}>{exactDate(p.dernier_contact)}</div></>
                      ) : <span style={{ color: "var(--faint)" }}>Jamais</span>}
                    </td>
                    {!apercu && (
                      <td className="px-4 py-2.5 hidden lg:table-cell" onClick={(e) => e.stopPropagation()}>
                        {p.prochain_rdv ? <span className="font-semibold" style={{ color: "var(--ink)" }}>{rdvFmt(p.prochain_rdv)}</span>
                          : p.statut === "a_relancer"
                            ? <button data-testid={`relancer-${p.id}`} onClick={() => nav(`/relation?patient=${p.id}`)} className="font-medium" style={{ color: "oklch(0.46 0.11 55)" }}>Relancer →</button>
                            : p.statut === "a_planifier"
                              ? <div>
                                  <div className="font-medium" style={{ color: "oklch(0.42 0.11 255)" }} data-testid={`asuivre-${p.id}`}>{p.prochain_suivi ? `À suivre · ${exactDate(p.prochain_suivi)}` : "À suivre"}{p.prochain_suivi_objet ? ` · ${p.prochain_suivi_objet}` : ""}</div>
                                  <button data-testid={`planifier-${p.id}`} onClick={() => openCreerRdv({ patientId: p.id })} className="text-[11.5px] font-medium" style={{ color: "var(--muted)" }}>Planifier →</button>
                                </div>
                              : <button data-testid={`planifier-${p.id}`} onClick={() => openCreerRdv({ patientId: p.id })} className="font-medium" style={{ color: "oklch(0.42 0.11 255)" }}>Planifier →</button>}
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <span className={`sx-badge ${st.cls}`}>{st.label}</span>
                        {(p.tags || []).map((tid) => { const t = tags.find((x) => x.id === tid); return t ? <span key={tid} className="inline-flex items-center rounded-full text-[11px] font-medium px-2 py-0.5" style={tagChipStyle(t.couleur)}>{t.titre}</span> : null; })}
                      </div>
                    </td>
                    <td className="px-4 py-2.5" onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button data-testid={`row-sms-${p.id}`} onClick={() => nav(`/relation?patient=${p.id}`)} className="sx-icon-btn" title="SMS"><MessageCircle className="w-4 h-4" /></button>
                        <button data-testid={`row-rdv-${p.id}`} onClick={() => openCreerRdv({ patientId: p.id })} className="sx-icon-btn" title="Créer RDV"><CalendarPlus className="w-4 h-4" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 0 && <div className="px-4 py-2 text-[11.5px]" style={{ color: "var(--muted-2)", borderTop: "1px solid var(--divider)" }}>{filtered.length} patient(s)</div>}
        </div>

        {/* Volet flottant : Nouveau patient (7a) ou Aperçu patient */}
        {newOpen
          ? <NewPatientPanel key="new" prefill={newPrefill} onClose={() => setNewOpen(false)}
              onReopen={(txt) => { setNewPrefill(txt); setNewOpen(true); }}
              onCreated={(p) => { load(); setHighlightId(p.id); }}
              nav={nav} openCreerRdv={openCreerRdv} secteurs={secteurs} tags={tags} />
          : apercu
            ? <ApercuPatient key={apercu} pid={apercu} onClose={() => setApercu(null)} nav={nav} ctmap={ctmapRef.current} tags={tags} secteurs={secteurs} />
            : null}
      </div>
    </div>
  );
}

function p_fmt(digitsStr) {
  return digitsStr.replace(/(\d{2})(?=\d)/g, "$1 ").trim();
}

function FilterSelect({ testid, value, onChange, placeholder, options }) {
  const applied = !!value;
  return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)}
      className="text-[12.5px] px-2.5 py-1.5 rounded-[7px]"
      style={{ border: applied ? "1px solid var(--border-input)" : "1px dashed var(--border-dashed)", background: applied ? "#fff" : "transparent", color: applied ? "var(--ink)" : "var(--muted)" }}>
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
    </select>
  );
}

export function ApercuPatient({ pid, onClose, nav, ctmap, tags, secteurs }) {
  const { openCreerRdv } = useCreerRdv();
  const [p, setP] = useState(null);
  useEffect(() => { api.get(`/patients/${pid}`).then((r) => setP(r.data)).catch(() => setP(null)); }, [pid]);
  if (!p) return <aside className="w-[420px] shrink-0 sx-card p-5" style={{ borderRadius: "14px" }}><div style={{ color: "var(--faint)" }}>Chargement…</div></aside>;

  const soins = p.soins || [];
  const now = new Date().toISOString();
  const futureRdv = (p.rdvs || []).filter((r) => r.date > now).sort((a, b) => a.date < b.date ? -1 : 1)[0];
  const pastDates = [...soins.map((s) => s.date), ...(p.rdvs || []).filter((r) => r.date <= now).map((r) => r.date), ...(p.echanges || []).map((e) => e.created_at)].filter(Boolean).sort();
  const lastContact = pastDates[pastDates.length - 1];
  const seuilMs = 3 * 30 * 86400000;
  const computedStatut = futureRdv ? "rdv_avenir" : (!lastContact ? "nouveau" : (Date.now() - new Date(lastContact).getTime() > seuilMs ? "a_relancer" : "a_planifier"));
  const st = STATUT[computedStatut] || STATUT.nouveau;
  const secNoms = (p.secteurs || []).map((sid) => (secteurs.find((s) => s.id === sid) || {}).nom).filter(Boolean).join(", ");
  const MiniCard = ({ label, value }) => (
    <div className="flex-1 rounded-[10px] p-2.5" style={{ background: "var(--bg-app)" }}>
      <div className="text-[11px]" style={{ color: "var(--muted-2)" }}>{label}</div>
      <div className="text-[13.5px] font-semibold mt-0.5" style={{ color: "var(--ink)" }}>{value}</div>
    </div>
  );

  return (
    <aside className="w-[420px] shrink-0 sx-card p-5 sticky self-start" style={{ borderRadius: "14px", boxShadow: "var(--sh-float)", top: "calc(var(--header-h, 96px) + 16px)", maxHeight: "calc(100vh - var(--header-h, 96px) - 32px)", overflowY: "auto" }} data-testid="apercu-patient">
      <div className="flex items-center justify-between text-[12.5px]">
        <button data-testid="apercu-close" onClick={onClose} className="inline-flex items-center gap-1" style={{ color: "var(--muted)" }}><X className="w-3.5 h-3.5" />Esc</button>
        <button data-testid="apercu-open-fiche" onClick={() => nav(`/patients/${p.id}`)} className="inline-flex items-center gap-1 font-medium" style={{ color: "var(--ink)" }}>Ouvrir la fiche <ArrowUpRight className="w-3.5 h-3.5" /></button>
      </div>

      <div className="mt-3">
        <div className="text-[24px] font-bold tracking-[-0.01em]" style={{ color: "var(--ink)" }}>{p.prenom} {p.nom}</div>
        <div className="flex flex-wrap items-center gap-2 mt-1.5">
          <span className="mono text-[11px]" style={{ color: "var(--id)" }}>{p.id}</span>
          <span className={`sx-badge ${st.cls}`}>{st.label}</span>
          {(p.tags || []).map((tid) => { const t = tags.find((x) => x.id === tid); return t ? <span key={tid} className="inline-flex items-center rounded-full text-[11px] font-medium px-2 py-0.5" style={tagChipStyle(t.couleur)}>{t.titre}</span> : null; })}
        </div>
      </div>

      <div className="flex items-center gap-2 mt-4">
        <button data-testid="apercu-rdv" onClick={() => openCreerRdv({ patientId: p.id })} className="sx-btn-primary flex-1 justify-center"><CalendarPlus className="w-[15px] h-[15px]" />Créer RDV</button>
        <a href={p.telephone ? `tel:${p.telephone}` : undefined} className="sx-btn-secondary" style={{ width: 40, padding: 0, justifyContent: "center", opacity: p.telephone ? 1 : 0.4 }} title="Appeler"><Phone className="w-4 h-4" /></a>
        <button onClick={() => nav(`/relation?patient=${p.id}`)} className="sx-btn-secondary" style={{ width: 40, padding: 0, justifyContent: "center" }} title="SMS"><MessageCircle className="w-4 h-4" /></button>
        <a href={p.email ? `mailto:${p.email}` : undefined} className="sx-btn-secondary" style={{ width: 40, padding: 0, justifyContent: "center", opacity: p.email ? 1 : 0.4 }} title="Email"><Mail className="w-4 h-4" /></a>
      </div>

      <div className="flex gap-2 mt-4">
        <MiniCard label="Prochain RDV" value={futureRdv ? rdvFmt(futureRdv.date) : "—"} />
        <MiniCard label="Dernier contact" value={lastContact ? relDate(lastContact) : "Jamais"} />
        <MiniCard label="Soins" value={soins.length} />
      </div>

      <div className="mt-4 space-y-1.5 text-[13px]">
        <div className="flex items-center gap-2" style={{ color: "var(--text-2)" }}><Phone className="w-3.5 h-3.5" style={{ color: "var(--muted-2)" }} />{p.telephone ? formatPhoneFR(p.telephone) : <span style={{ color: "var(--faint)" }}>Téléphone à renseigner</span>}</div>
        <div className="flex items-center gap-2" style={{ color: "var(--text-2)" }}><Mail className="w-3.5 h-3.5" style={{ color: "var(--muted-2)" }} />{p.email || <span style={{ color: "var(--faint)" }}>Email à renseigner</span>}</div>
        <div className="flex items-center gap-2" style={{ color: "var(--text-2)" }}><MapPin className="w-3.5 h-3.5" style={{ color: "var(--muted-2)" }} />{secNoms || <span style={{ color: "var(--faint)" }}>Secteur à renseigner</span>}</div>
      </div>

      <div className="mt-4">
        <div className="text-[11.5px] font-medium mb-1.5" style={{ color: "var(--muted-2)" }}>DERNIERS SOINS</div>
        {soins.length === 0 ? <div className="text-[13px]" style={{ color: "var(--faint)" }}>Aucun soin pour l'instant.</div> : (
          <div className="space-y-0.5">
            {soins.slice(0, 4).map((s) => (
              <button key={s.id} data-testid={`apercu-soin-${s.id}`} onClick={() => nav(`/fiche-soin/${s.id}`)} className="w-full flex items-center gap-2 text-left rounded-[8px] px-2 py-1.5 transition-colors"
                onMouseEnter={(e) => (e.currentTarget.style.background = "var(--row-hover)")} onMouseLeave={(e) => (e.currentTarget.style.background = "")}>
                <span className="mono text-[11.5px] shrink-0" style={{ color: "var(--id)" }}>{s.date ? new Date(s.date).toLocaleDateString("fr-FR") : "—"}</span>
                <span className="flex-1 truncate text-[13px]" style={{ color: s.titre ? "var(--ink)" : "var(--faint)" }}>{s.titre || "Sans titre"}</span>
                <ChevronRight className="w-3.5 h-3.5 shrink-0" style={{ color: "var(--muted-2)" }} />
              </button>
            ))}
          </div>
        )}
      </div>
    </aside>
  );
}
